import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { AccountService } from '@account-users';
import {
  DeliveryStatisticsActionTypes,
  GetAccounts,
  GetAccountsSuccess,
  GetAccountsFail,
  GetEmailStatistics,
  GetSmsStatistics,
} from '../actions/delivery-statistics.actions';
import * as emailActions from '../actions/email.actions';
import * as smsActions from '../actions/sms.actions';

@Injectable()
export class DeliveryStatisticsEffects {
  @Effect()
  getAccounts$ = this.actions$.pipe(
    ofType(DeliveryStatisticsActionTypes.GetAccounts),
    switchMap(({ payload: { searchTerm, loadAll } }: GetAccounts) =>
      this.service
        .fetchAccounts({ query: searchTerm, categories: [], loadAll })
        .pipe(
          map((accounts) =>
            accounts.sort((a, b) =>
              a.displayName.toLowerCase() > b.displayName.toLowerCase()
                ? 1
                : -1,
            ),
          ),
          map((accounts) => new GetAccountsSuccess(accounts)),
          catchError((error) => {
            this.toastr.error('Could not fetch accounts', 'Request failed');
            return [new GetAccountsFail(error)];
          }),
        ),
    ),
  );

  @Effect()
  getEmailStatistics$ = this.actions$.pipe(
    ofType(DeliveryStatisticsActionTypes.GetEmailStatistics),
    switchMap(({ payload: { timeSpans, accountId } }: GetEmailStatistics) => {
      const dateRange = this.getDateRange(timeSpans);

      return [
        new emailActions.GetStatistics({ dateRange, accountId }),
        new emailActions.GetStatisticsOverTime({ timeSpans, accountId }),
        new emailActions.GetDomainStatistics({ dateRange, accountId }),
        new emailActions.GetSendingsStatistics({ dateRange, accountId }),
      ];
    }),
  );

  @Effect()
  getSmsStatistics = this.actions$.pipe(
    ofType(DeliveryStatisticsActionTypes.GetSmsStatistics),
    switchMap(({ payload: { timeSpans, accountId } }: GetSmsStatistics) => {
      const dateRange = this.getDateRange(timeSpans);

      return [
        new smsActions.GetStatisticsOverview({ dateRange, accountId }),
        new smsActions.GetCountryCodes({ dateRange, accountId }),
        new smsActions.GetTotalTimeline({ timeSpans, accountId }),
      ];
    }),
  );

  getDateRange(timeSpans: number[]) {
    return {
      from: timeSpans[0],
      to: timeSpans[timeSpans.length - 1],
    };
  }

  constructor(
    private readonly actions$: Actions,
    private readonly service: AccountService,
    private readonly toastr: ToastrService,
  ) {}
}
